<template>
  <page-with-sidebar class="page-support"
                     :sidebar-mobile-title="$t('digitalServices.mobileMenuTitle')">
    <div slot="sidebar">
      <sidebar-with-contact ref="sidebar"
                            sidebar-type="support"
                            :loading="dataLoading"
                            :customer-accounts="userAccounts"
                            :block-selection="sidebarSelectionBlocked"
                            @select-account="handleAccountSelect"
                            @selected="handleMenuItemSelect"/>
    </div>
    <page-layout :has-header="hasHeader">
      <template #header>
        <transition name="fade" mode="out-in">
          <sidebar-with-contact-nav-tabs :selected-tab="selectedService"
                                         menu-type="support"
                                         prevent-first-tab-select
                                         ref="supportPageNavTabs"
                                         class="tab-nav"
                                         @tab-change="handleMobileMenuSelect"/>
        </transition>
      </template>
      <transition name="fade" mode="out-in">
        <section class="page-content" v-if="mainComponent" :key="componentKey">
          <div class="main-content">
            <component :is="mainComponent" :user-info="selectedUserSubscriptionInfo"/>
          </div>
        </section>
      </transition>
    </page-layout>
  </page-with-sidebar>
</template>

<script>
import PageWithSidebar from '@/components/common/main-layout/page-with-sidebar'
import PageLayout from '@/components/common/main-layout/page-layout'
import errorMessageHandler from '@/mixins/errorMessageHandler'
import SupportPlaceholder from '@/components/support/support-placeholder'
import SidebarWithContact from '@/components/common/navigation/sidebar-with-contact'
import SidebarWithContactNavTabs from '@/components/common/navigation/sidebar-with-contact-nav-tabs'
import AgentContact from '@/components/support/agent-contact'
import PosMap from '@/components/support/pos-map'
import { addDataLayerEvent } from '@/services/dataLayer'
import { addUserIdDataLayerEvent } from '@/utils/addUserIdDataLayerEvent'

export default {
  name: 'page-support',
  components: { PosMap, AgentContact, SidebarWithContactNavTabs, SupportPlaceholder, SidebarWithContact, PageLayout, PageWithSidebar },
  data () {
    return {
      selectedService: '',
      mainComponent: '',
      componentKey: '',
      dataLoading: false,
      sidebarSelectionBlocked: false,
      userInfo: {},
      selectedUserSubscriptionInfo: {},
      userAccounts: [],
      hasHeader: false,
      userEmail: ''
    }
  },
  mixins: [errorMessageHandler],
  methods: {
    fetchUserInfo () {
      this.dataLoading = true
      this.$apiRequest.get('user/info')
        .then(response => {
          this.userInfo = response.data || {}
          this.userAccounts = response.data.mtmCustomers || []
          this.userEmail = response.data?.uid
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.dataLoading = false
        })
    },
    handleAccountSelect (payload) {
      this.selectedAccount = payload
      this.selectedUserSubscriptionInfo = { ...payload, ...{ userEmail: this.userEmail } } || {}

      if (this.$refs.supportPageNavTabs) {
        this.$refs.supportPageNavTabs.markFirstTab()
      }
    },
    handleMobileMenuSelect (tabName) {
      this.$refs.sidebar.selectMenuItem({ name: tabName })
    },
    handleMenuItemSelect (menuPayload, availableServices) {
      const { name } = menuPayload

      // if mobile tab navigation is visible, marks the specific tab when navigation occurs outside of the tabs
      if (this.$refs.supportPageNavTabs) {
        this.$refs.supportPageNavTabs.markTabUsingName(name)
      }

      // rerender components
      this.componentKey = new Date().getTime()

      this.selectedService = name
      this.mainComponent = this.getMainComponent(name, availableServices)
      if (name === 'MAP') {
        addDataLayerEvent({
          event: 'screen',
          name: 'maps_pos'
        })
        addUserIdDataLayerEvent()
      }
    },
    getMainComponent (name) {
      if (!name) return ''
      else if (name === 'AGENT') return 'agent-contact'
      else if (name === 'MAP') return 'pos-map'
      return 'support-placeholder'
    },
    debounce (fn, timeout) {
      if (this.debounceTimeout) clearTimeout(this.debounceTimeout)
      this.debounceTimeout = setTimeout(() => {
        fn()
      }, timeout)
    },
    handleResize () {
      this.hasHeader = window.innerWidth < 1024
    }
  },
  created () {
    this.fetchUserInfo()
    this.handleResize()
    window.addEventListener('resize', () => this.debounce(this.handleResize, 50))
    addDataLayerEvent({
      event: 'screen',
      name: 'support'
    })
    addUserIdDataLayerEvent()
  }
}
</script>

<style scoped lang="scss">
.page-support {
  padding: 0;
}

.tab-nav {
  margin-bottom: -2.9rem;
  height: 51px;

  @media (map_get($breakpoints, sm)) {
    height: 51px;
  }
}
</style>
